import { useEffect, useRef, useState } from "react";
import { hideAd, recordAdClick, recordAdImpression } from "../../functions/ads";
import useClickOutside from "../../helpers/clickOutside";
import { Dots } from "../../svg";
import "./style.css";

const API_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : "http://localhost:8000";

function mediaSrc(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API_URL}${url}`;
}

export default function SponsoredAd({ ad, user, onHide }) {
  const cardRef = useRef(null);
  const menuRef = useRef(null);
  const recordedRef = useRef(false);
  const [showMenu, setShowMenu] = useState(false);
  const [hiding, setHiding] = useState(false);

  useClickOutside(menuRef, () => setShowMenu(false));

  useEffect(() => {
    const node = cardRef.current;
    if (!node || !ad?._id || !user?.token || recordedRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && entry.intersectionRatio >= 0.5 && !recordedRef.current) {
          recordedRef.current = true;
          recordAdImpression(ad._id, user.token);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ad?._id, user?.token]);

  const openDestination = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!ad?.destinationUrl) return;
    if (user?.token) {
      await recordAdClick(ad._id, user.token);
    }
    window.open(ad.destinationUrl, "_blank", "noopener,noreferrer");
  };

  const handleNotInterested = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!ad?._id || hiding) return;
    setHiding(true);
    setShowMenu(false);
    if (typeof onHide === "function") {
      onHide(ad._id);
    }
    try {
      if (user?.token) {
        await hideAd(ad._id, user.token);
      }
    } catch (error) {
      console.error("Failed to hide ad:", error.message);
    }
  };

  if (!ad) return null;

  return (
    <article className="post sponsored_ad" ref={cardRef} aria-label={`Sponsored: ${ad.title}`}>
      <div className="post_header">
        <div className="post_header_left">
          <div className="sponsored_ad_avatar" aria-hidden="true">
            {ad.title?.charAt(0)?.toUpperCase() || "A"}
          </div>
          <div className="header_col">
            <div className="post_profile_name">
              {ad.title}
              <span className="sponsored_label">Sponsored</span>
            </div>
            <div className="post_profile_privacy_date">Promoted content</div>
          </div>
        </div>
        <div className="sponsored_ad_menu_wrap" ref={menuRef}>
          <button
            type="button"
            className="sponsored_ad_menu_btn hover1"
            aria-label="Ad options"
            aria-haspopup="menu"
            aria-expanded={showMenu}
            onClick={() => setShowMenu((prev) => !prev)}
          >
            <Dots color="#828387" />
          </button>
          {showMenu && (
            <ul className="sponsored_ad_menu" role="menu">
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="sponsored_ad_menu_item"
                  onClick={handleNotInterested}
                  disabled={hiding}
                >
                  <span className="sponsored_ad_menu_title">Not Interested</span>
                  <span className="sponsored_ad_menu_sub">Hide this ad</span>
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {ad.description ? <div className="sponsored_ad_text">{ad.description}</div> : null}

      {ad.mediaUrl && (
        <button type="button" className="sponsored_ad_media" onClick={openDestination}>
          {ad.mediaType === "video" ? (
            <video
              src={mediaSrc(ad.mediaUrl)}
              controls
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img src={mediaSrc(ad.mediaUrl)} alt={ad.title} />
          )}
        </button>
      )}

      <div className="sponsored_ad_footer">
        <a
          href={ad.destinationUrl}
          className="sponsored_ad_cta"
          onClick={openDestination}
          rel="noopener noreferrer"
        >
          {ad.ctaText || "Learn more"}
        </a>
      </div>
    </article>
  );
}
